import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  TemplateRef,
  untracked,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { menuBar, MenuBarService } from '@softapps/core';
import {
  CalendarViewType,
  RosterContent,
  RosterService,
  RosterWithDepartmentHeadHostComponent,
} from '@softapps/pms/roster';
import { MbscTimelineColumnWidth } from '@mobiscroll/angular';
import {
  ButtonComponent,
  ContextMenuService,
  DsContextMenuComponent,
  DsContextMenuDividerComponent,
  DsContextMenuItemComponent,
  DsSplitButtonComponent,
  DsSplitButtonOptionComponent,
  IconComponent,
  SoftSlotDirective,
  ToggleComponent,
  ToggleGroupComponent,
  TranslatePipe,
  translator,
} from '@softline/ui-core';
import {
  SelectDsComponent,
  SelectDsMultiComponent,
  SelectDsOptionComponent,
} from '@softline/ui-core/select';

@Component({
  selector: 'soft-softline-roster-menu-bar',
  templateUrl: './menu-bar.component.html',
  styles: `
    :host {
      display: contents;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    IconComponent,
    SoftSlotDirective,
    DsSplitButtonComponent,
    DsSplitButtonOptionComponent,
    SelectDsComponent,
    SelectDsMultiComponent,
    SelectDsOptionComponent,
    ToggleGroupComponent,
    ToggleComponent,
    TranslatePipe,
    DsContextMenuComponent,
    DsContextMenuItemComponent,
    DsContextMenuDividerComponent,
  ],
})
export class SoftlineMenuBarComponent {
  readonly t = translator();
  readonly menuBarService = inject(MenuBarService);
  readonly service = inject(RosterService);
  readonly hostComponent = inject(RosterWithDepartmentHeadHostComponent);
  readonly #contextMenu = inject(ContextMenuService);
  readonly #viewContainerRef = inject(ViewContainerRef);

  readonly selectedEventTypeFilter = this.hostComponent.eventTypesFilter;

  readonly viewTypeOptions = computed(() => [
    { v: 'day' as CalendarViewType, l: this.t('#PMS_ROSTER.VIEW_TYPE_DAY')() },
    { v: 'workWeek' as CalendarViewType, l: this.t('#PMS_ROSTER.VIEW_TYPE_WORK_WEEK')() },
    { v: 'week' as CalendarViewType, l: this.t('#PMS_ROSTER.VIEW_TYPE_WEEK')() },
    { v: 'month' as CalendarViewType, l: this.t('#PMS_ROSTER.VIEW_TYPE_MONTH')() },
    {
      v: 'planMonth' as CalendarViewType,
      l: this.t('#PMS_ROSTER.VIEW_TYPE_PLAN_MONTH')(),
    },
  ]);
  readonly columnWidthOptions = computed(() => [
    {
      v: 'small' as MbscTimelineColumnWidth,
      l: this.t('#PMS_ROSTER.COLUMN_WIDTH_SMALL')(),
    },
    {
      v: 'large' as MbscTimelineColumnWidth,
      l: this.t('#PMS_ROSTER.COLUMN_WIDTH_NORMAL')(),
    },
    {
      v: 'xxxlarge' as MbscTimelineColumnWidth,
      l: this.t('#PMS_ROSTER.COLUMN_WIDTH_LARGE')(),
    },
  ]);
  readonly detailOptions = computed(() => [
    { v: 'compact' as const, l: this.t('#PMS_ROSTER.DETAILS_COMPACT')() },
    { v: 'all' as const, l: this.t('#PMS_ROSTER.DETAILS_ALL')() },
  ]);

  readonly eventTypeOptions = computed(() => [
    { v: 'tickets', l: this.t('#PMS_ROSTER.FILTER_TICKETS_OBJECTS_TASKS')() },
    { v: 'appointments', l: this.t('#PMS_ROSTER.FILTER_APPOINTMENTS')() },
    { v: 'holidays', l: this.t('#PMS_ROSTER.FILTER_ABSENCES')() },
  ]);

  readonly viewTypeLabel = computed(
    () =>
      this.viewTypeOptions().find((o) => o.v === this.hostComponent.calendarViewType())
        ?.l ?? ''
  );
  readonly columnWidthLabel = computed(
    () =>
      this.columnWidthOptions().find(
        (o) => o.v === this.hostComponent.calendarColumnWidth()
      )?.l ?? ''
  );
  readonly detailLabel = computed(
    () =>
      this.detailOptions().find((o) => o.v === this.hostComponent.calendarDetails())?.l ??
      ''
  );
  readonly detailPillOptions = computed(() =>
    this.detailOptions().map((o) => ({
      label: o.l,
      value: o.v,
      icon: o.v === 'compact' ? 'fa-regular fa-compress' : 'fa-regular fa-table-list',
    }))
  );

  readonly selectedEvents = computed(() => {
    return this.service.selectedEvents();
  });

  readonly showTicketEventOptions = computed(() => {
    return (
      this.selectedEvents().length === 1 &&
      this.selectedEvents().every((o: RosterContent) => {
        const details = o?.additionalData?.['additionaldetail'];
        if (!details || details.length < 1) return false;
        return details[0].tabname === 'TICKET';
      })
    );
  });

  readonly showProjectEventOptions = computed(() => {
    return (
      this.selectedEvents().length === 1 &&
      this.selectedEvents().every((o: RosterContent) => {
        const details = o?.additionalData?.['additionaldetail'];
        if (!details || details.length < 1) return false;
        return details[0].tabname === 'OBJECT';
      })
    );
  });

  readonly isAppointment = computed(() => {
    return (
      this.selectedEvents().length >= 1 &&
      this.selectedEvents().some(
        (o: RosterContent) => o?.additionalData?.type === 'APPOINTMENT'
      )
    );
  });

  readonly isTicket = computed(() => {
    const selectedEvents = this.selectedEvents();
    return (
      selectedEvents.length >= 1 &&
      selectedEvents.every(
        (o: RosterContent) =>
          o?.additionalData?.['additionaldetail']?.[0]?.tabname === 'TICKET'
      )
    );
  });

  readonly isProject = computed(() => {
    const selectedEvents = this.selectedEvents();
    return (
      selectedEvents.length >= 1 &&
      selectedEvents.every(
        (o: RosterContent) =>
          o?.additionalData?.['additionaldetail']?.[0]?.tabname === 'OBJECT'
      )
    );
  });

  readonly multiSelect = computed(() => this.selectedEvents().length > 1);

  hasClipboardEvent(): boolean {
    return this.service.copyEvents().length > 0;
  }

  readonly modifierKey = /mac|iphone|ipad|ipod/i.test(
    navigator.userAgent || navigator.platform || ''
  )
    ? '⌘'
    : 'Ctrl';

  private readonly eventMenuTemplate = viewChild.required('eventMenu', {
    read: TemplateRef,
  });
  private readonly calendarMenuTemplate = viewChild.required('calendarMenu', {
    read: TemplateRef,
  });
  private readonly statusMenuTemplate = viewChild.required('statusSubmenu', {
    read: TemplateRef,
  });

  readonly parameterTrailingMenuBarTemplate = viewChild.required(
    'parameterTrailingMenuBar',
    {
      read: TemplateRef,
    }
  );
  readonly viewTrailingMenuBarTemplate = viewChild.required('viewTrailingMenuBar', {
    read: TemplateRef,
  });
  readonly viewLeadingMenuBarTemplate = viewChild.required('viewLeadingMenuBar', {
    read: TemplateRef,
  });
  readonly selectionLeadingMenuBarTemplate = viewChild.required(
    'selectionLeadingMenuBar',
    {
      read: TemplateRef,
    }
  );
  readonly selectionTrailingMenuBarTemplate = viewChild.required(
    'selectionTrailingMenuBar',
    {
      read: TemplateRef,
    }
  );

  constructor() {
    menuBar({
      key: 'pms/roster/view',
      title: this.t('#PMS_ROSTER.VIEW'),
      template: this.parameterTrailingMenuBarTemplate,
      separated: true,
      containerStyle: 'plain',
      align: 'trailing',
      order: 3,
    });
    menuBar({
      key: 'pms/roster/view',
      title: this.t('#PMS_ROSTER.VIEW'),
      template: this.viewLeadingMenuBarTemplate,
      separated: true,
      containerStyle: 'plain',
      order: 2,
    });
    menuBar({
      key: 'pms/roster/view',
      title: this.t('#PMS_ROSTER.VIEW'),
      template: this.viewTrailingMenuBarTemplate,
      separated: true,
      containerStyle: 'plain',
      align: 'trailing',
      order: 2,
    });
    menuBar({
      key: 'softline/roster/selection',
      title: this.t('#PMS_ROSTER.SELECTION'),
      template: this.selectionLeadingMenuBarTemplate,
      separated: true,
      containerStyle: 'plain',
      visible: computed(() => this.selectedEvents().length > 0),
      order: 3,
    });
    menuBar({
      key: 'softline/roster/selection',
      title: this.t('#PMS_ROSTER.SELECTION'),
      template: this.selectionTrailingMenuBarTemplate,
      separated: true,
      containerStyle: 'plain',
      visible: computed(() => this.selectedEvents().length > 0),
      align: 'trailing',
      order: 3,
    });

    effect(() => {
      const selectedEvents = this.selectedEvents();
      if (selectedEvents.length > 0) {
        untracked(() => {
          this.menuBarService.activateKey('softline/roster/selection');
        });
      } else if (selectedEvents.length === 0) {
        untracked(() => {
          this.menuBarService.activateIndex(0);
        });
      }
    });

    effect(() => {
      this.service.eventMenu.set(this.eventMenuTemplate());
      this.service.calendarMenu.set(this.calendarMenuTemplate());
    });
  }

  setDetailOption(value: unknown): void {
    if (value === 'compact' || value === 'all')
      this.hostComponent.calendarDetails.set(value);
  }

  setEventTypes(types: string[] | null): void {
    const next = types ?? [];
    this.hostComponent.eventTypesFilter.set(
      next.length ? next : [...this.hostComponent.eventTypesFilter()]
    );
  }

  onNewEntryOption(action: unknown): void {
    if (typeof action === 'string') this.service.handleContextMenuAction(action);
  }

  openStatusMenu(anchor: HTMLElement): void {
    const rect = anchor.getBoundingClientRect();
    this.#contextMenu.open({
      x: rect.left,
      y: rect.bottom + 4,
      menu: this.statusMenuTemplate(),
      viewContainerRef: this.#viewContainerRef,
    });
  }
}
